import * as React from "react";
import {FC, useCallback} from "react";
import {
   FilterFindManyBrandProductInput,
   LocalizationEntry,
   ProductPaginationFragment,
   SortFindManyBrandProductInput,
   useBrandProductListPaginationLazyQuery,
   useBrandProductListSourcesQuery
} from "../../Queries";
import {RangeFilter, RangeFilterValue, RangeListColumn, RangeSort, RangeTab} from "../../components/rangelist/RangeListTypes";
import {Thumbnails} from "../../components/Thumbnails";
import {LinearProgress} from "@mui/material";
import {RangeList} from "../../components/rangelist/RangeList";

type BrandProductsListProps = {
   brandHouseId: string
   onProductSelect: (product: ProductPaginationFragment) => void
   defaultSearch?: string
   selectedGroupIds?: string[]
   inDialog?: boolean
   onTabChange?: (groupId: string) => void
   onSearchChange?: (search: string) => void
   filterActiveGroups?: boolean
}

const PAGE_SIZE = 20;

export const BrandProductsList: FC<BrandProductsListProps> = ({brandHouseId, onProductSelect, defaultSearch, selectedGroupIds, inDialog, onTabChange, onSearchChange, filterActiveGroups}) => {
   const [fetchMore] = useBrandProductListPaginationLazyQuery({
      fetchPolicy: "no-cache"
   });
   const {data} = useBrandProductListSourcesQuery({
      variables: {brandHouseId},
      fetchPolicy: "no-cache"
   })

   let columns: RangeListColumn<ProductPaginationFragment>[] = [{
      id: 'image',
      label: "Image",
      get: (product) => {
         return product._id
      },
      render: (id, product) => {
         let imageUrls = product?.imageUrls || [];
         return <Thumbnails size={100} imageUrls={imageUrls.filter(img => !!img) as string[]} maxImages={3}/>
      },
      width: 5
   }, {
      id: 'name',
      field: 'nameLocalized',
      label: "Name",
      render: (names: LocalizationEntry[], row) => {
         const firstValue = names?.find(name => !!name.value)
         const languages = names?.filter(name => !!name.value)?.map(name => name.locale).join(',')
         return <div>{firstValue?.value} ({languages})</div>
      },
      width: 20
   }, {
      id: 'style',
      field: 'style',
      label: "Style",
      render: (names: string, row) => {
         return <div>{names}</div>
      },
      width: 5
   }]

   const sortSpec: RangeSort<SortFindManyBrandProductInput>[] = [{
      id: 'CreatedAsc',
      label: 'Created (oldest first)',
      value: SortFindManyBrandProductInput._ID_ASC
   }, {
      id: 'CreatedDesc',
      label: 'Created (newest first)',
      value: SortFindManyBrandProductInput._ID_DESC
   }]

   const filters: RangeFilter<any>[] = [{
      id: 'groups',
      label: 'Groups',
      options: data?.BrandProductGroups?.map(group => {
         return {
            filter: {
               groupId: group._id
            },
            label: group.name
         }
      }) || []
   }]

   // Filter groups if filterActiveGroups is true
   const filteredGroups = filterActiveGroups 
      ? data?.BrandProductGroups?.filter(group => group.active || group.activePromotions) 
      : data?.BrandProductGroups;

   const tabsSpec: RangeTab[] = filteredGroups?.map(group => {
      return {
         id: String(group._id),
         label: group.name,
         filters: [{
            filterId: 'groups',
            values: [group.name]
         }]
      }
   }) || [{
      id: 'all',
      label: 'All',
      filters: []
   }];

   const buildEffectiveFilter = useCallback((filter: any): FilterFindManyBrandProductInput => {
      // When filterActiveGroups is true and no specific filter is applied yet,
      // we need to set a default filter for active groups
      let activeGroupsFilter = {};
      if (filterActiveGroups && !filter?.groupId && filteredGroups?.length) {
         // Get IDs of active groups or groups with active promotions
         const activeGroupIds = filteredGroups.map(group => group._id);
         if (activeGroupIds.length) {
            activeGroupsFilter = {
               groupId: { $in: activeGroupIds }
            };
         }
      }

      return {
         ...filter,
         ...activeGroupsFilter,
         _operators: {
            brandHouseId: {
               ...!!brandHouseId ? {in: [brandHouseId]} : {ne: '601c19a2c6dac20032263343'}
            },
         }
      };
   }, [filterActiveGroups, filteredGroups, brandHouseId]);

   const onRequestData = useCallback(async (search: string | undefined, sort: string | undefined, page: number, perPage: number, filter?: any) => {
      const effectiveFilter = buildEffectiveFilter(filter);

      const {data} = await fetchMore({
         variables: {
            page: page,
            sort: (sort as SortFindManyBrandProductInput) || SortFindManyBrandProductInput._ID_DESC,
            perPage: perPage,
            search: search,
            filter: effectiveFilter
         }
      });
      return {
         data: data?.BrandProductsPagination?.items || [],
         totalCount: data?.BrandProductsPagination?.count || 0
      }
   }, [fetchMore, buildEffectiveFilter]);

   const onSelectAll = useCallback(async (filter?: any): Promise<string[]> => {
      const effectiveFilter = buildEffectiveFilter(filter);

      const {data} = await fetchMore({
         variables: {
            page: 1,
            sort: SortFindManyBrandProductInput._ID_DESC,
            perPage: 10000,
            search: undefined,
            filter: effectiveFilter
         }
      });
      return data?.BrandProductsPagination?.items?.map(item => item._id) || [];
   }, [fetchMore, buildEffectiveFilter]);

   const onSelectProduct = (product: ProductPaginationFragment) => {
      onProductSelect(product);
   }

   // Check if there are groups available based on the filtering option
   const availableGroups = filterActiveGroups
      ? filteredGroups?.length
      : data?.BrandProductGroups?.length;

   if (!availableGroups) {
      return <LinearProgress/>
   }

   // Convert selectedGroupIds to initialFilters format
   const initialFiltersValue: RangeFilterValue[] | undefined = selectedGroupIds && selectedGroupIds.length > 0
      ? [{
         filterId: 'groups',
         values: selectedGroupIds.map(id =>
            data?.BrandProductGroups?.find(g => g._id === id)?.name || ''
         ).filter(name => name !== ''),
         requireAll: false
      }]
      : undefined;

   return <RangeList
      selections={true}
      globalId={"viewBrandProductList"}
      tabs={inDialog ? undefined : tabsSpec}
      itemsPerPage={PAGE_SIZE}
      columns={columns}
      initialSearch={defaultSearch}
      initialFilters={initialFiltersValue}
      filters={filters}
      onRequestData={onRequestData}
      onSelectAll={onSelectAll}
      sorts={sortSpec}
      options={{
         itemHeight: 105,
         height: inDialog ? 500 : undefined,
         disableUrlState: inDialog || false
      }}
      onSelectRow={onSelectProduct}
      onTabChange={onTabChange}
      onSearchChange={onSearchChange}
   />

}
